"use client";

import { memo, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { IDiscipline } from "@/sanity/pages/PageCourseGalleryTemplate";
import { CheckIcon, ChevronIcon, MetaDot, PipeChip } from "./_shared";

interface DisciplinesSectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  subtitle?: string;
  disciplines?: IDiscipline[];
}

// Figma 3780:19370. Card slots are 510px wide and sit on a 532px centre-to-centre
// pitch; the track slides so the active slot's centre lands on the section's.
const SLOT_REM = 510 / 8;
const STEP_REM = 532 / 8;

// The avatar picker fits its row at 1920, but not on a phone. There the row
// becomes a track that slides inside a masked viewport, keeping the active
// avatar on the centre line: 8.125rem avatars on a 1.5rem gap.
const AVATAR_REM = 8.125;
const AVATAR_STEP_REM = AVATAR_REM + 1.5;

// ── Infinite loop ───────────────────────────────────────────────────────────
// Both tracks carry three copies of the list and ride an unbounded position, so
// advancing past the last discipline slides straight on into the first instead
// of rewinding across the whole row. Once a slide settles, the position is
// re-centred on the middle copy — a jump of exactly one list length, which is
// an identical arrangement, so with the transition suppressed for a frame it
// cannot be seen.
const STRIP_SETS = 3;
const SLIDE_MS = 500; // must match the duration-500 on both tracks

// ── Avatar autoplay ─────────────────────────────────────────────────────────
// Each avatar holds for 5s, its ring drawing clockwise from 12 o'clock, then
// hands over to the next discipline and wraps at the end — endlessly.
const AUTOPLAY_MS = 5000;

// Ring drawn in the 65px picker box from the design; r=32 leaves room for the
// 1px stroke, matching the static border Figma puts on the active avatar.
const RING_R = 32;
const RING_C = 2 * Math.PI * RING_R;

// Memoised: while the track is being dragged the section re-renders every
// frame, and the card subtrees are by far the heaviest part of it.
const DisciplineCard = memo(function DisciplineCard({ item, active }: { item: IDiscipline; active: boolean }) {
  const meta = [item.category, item.classesLabel, item.durationLabel].filter(Boolean) as string[];

  // Geometry is constant — the border and padding are always there and only
  // the border's colour, the card's opacity and a subtle scale animate. Those
  // are paint/composite-only, so the 500ms slide never forces a relayout of
  // the card contents (animating `padding`/`border-width` via transition-all
  // reflowed every card on every frame, which is what made the slide stutter).
  return (
    <div
      className={`rounded-[1.5rem] border p-[0.75rem] transition-[border-color,opacity,transform] duration-500 ease-[cubic-bezier(0.65,0,0.35,1)] ${
        active ? "border-[#96C7E5]/60" : "scale-[0.98] border-transparent opacity-40"
      }`}>
      <div className="flex w-full flex-col items-start gap-[2.75rem] rounded-[0.75rem] bg-white/[0.08] p-[1rem]">
        {/* Cover */}
        <div className="relative h-[20rem] w-full overflow-x-clip rounded-[0.5rem] bg-white/[0.04] lg:h-[27.125rem]">
          {item.cover && (
            <Image
              src={item.cover.src}
              alt={item.headline ?? ""}
              fill
              quality={100}
              sizes="(max-width: 992px) 90vw, 60rem"
              placeholder={item.cover.lqip ? "blur" : undefined}
              blurDataURL={item.cover.lqip}
              className="object-cover"
            />
          )}
        </div>

        {/* Body */}
        <div className="flex w-full flex-col items-start gap-[3rem] px-[1.5rem] pb-[1.5rem] lg:gap-[4rem] lg:px-[2.5rem] lg:pb-[2.5rem]">
          <div className="flex w-full flex-col items-start gap-[2.25rem]">
            <div className="flex w-full flex-col items-start gap-[2.125rem]">
              {meta.length > 0 && (
                <div className="flex flex-wrap items-center gap-[1.5rem]">
                  {meta.map((entry, i) => (
                    <span
                      key={i}
                      className="flex items-center gap-[1.5rem]">
                      {i > 0 && <MetaDot />}
                      <span className="font-geist text-[1.5rem] text-white/60 uppercase lg:text-[1.75rem]">
                        {entry}
                      </span>
                    </span>
                  ))}
                </div>
              )}

              {item.headline && (
                <p className="m-0 font-geist text-[3rem] leading-[1.1] tracking-[-0.06rem] text-white lg:w-[43.375rem] lg:text-[4rem] lg:tracking-[-0.08rem]">
                  {item.headline}
                </p>
              )}
            </div>

            {/* What you'll learn */}
            {item.bullets && item.bullets.length > 0 && (
              <div className="flex w-full flex-col items-start justify-center gap-[0.75rem]">
                {item.bullets.map((bullet, i) => (
                  <div
                    key={i}
                    className="flex w-full items-center gap-[1.75rem] rounded-[0.5rem] bg-white/[0.04] py-[0.75rem] pr-[2rem] pl-[0.75rem] lg:pr-[3.125rem]">
                    <span className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-[0.375rem] border border-[#6B9EBD]/10 bg-[#98D5F9]/10">
                      <CheckIcon className="h-[1.282rem] w-[1.875rem] text-[#98D8FF]" />
                    </span>
                    <p className="m-0 font-geist text-[1.75rem] leading-[1.39] tracking-[-0.04rem] text-white opacity-80 lg:text-[2rem]">
                      {bullet}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mentor credit — only the active card shows it in the design.
              Always rendered and faded so the card's height never jumps the
              instant `active` flips mid-slide. */}
          {(item.mentorName || item.mentorRole) && (
            <div
              className={`flex flex-col items-start gap-[0.5rem] transition-opacity duration-500 ${
                active ? "opacity-100" : "opacity-0"
              }`}>
              {item.mentorName && (
                <p className="m-0 font-geist text-[2rem] text-white lg:text-[2.25rem]">{item.mentorName}</p>
              )}
              {item.mentorRole && (
                <p className="m-0 font-geist text-[1.75rem] text-white/60 lg:text-[2rem]">{item.mentorRole}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default function DisciplinesSection({ chip, title, subtitle, disciplines }: DisciplinesSectionProps) {
  const items = disciplines ?? [];
  const count = items.length;

  // Position along the tripled strip, not an index into `items`: it may sit in
  // any of the three copies, and `active` is read back out of it. Opens on the
  // middle discipline of the middle copy, matching the Figma resting state.
  const [pos, setPos] = useState(() => count + (count ? Math.floor((count - 1) / 2) : 0));
  // True for the single frame the track is being re-centred, so the jump back
  // to the middle copy is not animated.
  const [recentring, setRecentring] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [inView, setInView] = useState(false);

  const active = count ? ((pos % count) + count) % count : 0;
  // Where the active discipline sits in the middle copy — the slot the desktop
  // picker shows, since it renders that copy alone.
  const middle = count + active;

  const pickerRef = useRef<HTMLDivElement>(null);
  const ringRefs = useRef<(SVGCircleElement | null)[]>([]);
  // Milliseconds already served by the current avatar. Held in a ref so that
  // pausing (hover / scrolling away) re-runs the loop effect without losing the
  // progress the ring has already drawn.
  const elapsedRef = useRef(0);

  // The chevrons and autoplay just walk the position; nothing wraps, which is
  // what keeps the travel continuous in both directions. Clicking an avatar or
  // card moves to that strip slot, so the track always takes the short way
  // round — the copy you clicked is the one next to you.
  const step = (delta: number) => setPos((current) => current + delta);

  // ── Dragging ──────────────────────────────────────────────────────────────
  // The cards track can be grabbed and thrown. While the pointer is down the
  // drag offset rides on top of the settled transform with the transition off;
  // on release the nearest slot wins, or a quick flick advances one. Drag only
  // engages past a small threshold, and capturing the pointer at that moment
  // retargets the eventual click at the wrapper — which is what keeps a drag
  // from also "clicking" the card it is released over, while plain clicks
  // still reach the cards untouched.
  const [dragging, setDragging] = useState(false);
  const [dragPx, setDragPx] = useState(0);
  const dragRef = useRef<{ id: number; startX: number; startT: number; moved: boolean } | null>(null);
  const dragFrame = useRef(0);

  const onDragDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    dragRef.current = { id: e.pointerId, startX: e.clientX, startT: e.timeStamp, moved: false };
  };

  const onDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(dx) <= 6) return;
      drag.moved = true;
      e.currentTarget.setPointerCapture(drag.id);
      setDragging(true);
    }
    // One state commit per frame at most — pointermove can outrun the display.
    cancelAnimationFrame(dragFrame.current);
    dragFrame.current = requestAnimationFrame(() => setDragPx(dx));
  };

  const onDragEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.id) return;
    dragRef.current = null;
    if (!drag.moved) return;
    cancelAnimationFrame(dragFrame.current);
    setDragging(false);
    setDragPx(0);

    const dx = e.clientX - drag.startX;
    const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
    // Slot pitch under the pointer: the 532px-design step on desktop, one
    // viewport-wide slot plus its 1.5rem gap on mobile (slot = 100vw − 4rem).
    const stepPx = window.matchMedia("(min-width: 993px)").matches
      ? STEP_REM * remPx
      : window.innerWidth - 2.5 * remPx;
    // Nearest slot, clamped to one list length so the settle stays inside the
    // tripled strip; a flick that never crossed a midpoint still moves one.
    let delta = Math.round(-dx / stepPx);
    if (delta === 0) {
      const velocity = dx / Math.max(1, e.timeStamp - drag.startT); // px/ms
      if (Math.abs(velocity) > 0.5) delta = -Math.sign(velocity);
    }
    setPos((current) => current + Math.max(-count, Math.min(count, delta)));
  };

  // Only run the timer while the picker is actually on screen.
  useEffect(() => {
    const node = pickerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.2 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Once a slide has finished, slip the position back onto the middle copy.
  // Cancelling on every change means a burst of clicks only re-centres after
  // the last one lands.
  useEffect(() => {
    if (count === 0) return;
    const centred = count + (((pos % count) + count) % count);
    if (centred === pos) return;

    const timer = setTimeout(() => {
      setRecentring(true);
      setPos(centred);
    }, SLIDE_MS);
    return () => clearTimeout(timer);
  }, [pos, count]);

  // Give the browser a painted frame with the transition off before restoring
  // it, otherwise it animates the re-centring jump it was meant to hide.
  useEffect(() => {
    if (!recentring) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setRecentring(false));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [recentring]);

  // Changing discipline — and only that — restarts the countdown and blanks the
  // rings. Declared before the loop below so it wins the race on an `active`
  // change: React runs the old cleanup first, then these effects in order.
  useEffect(() => {
    elapsedRef.current = 0;
    ringRefs.current.forEach((ring) => {
      if (ring) ring.style.strokeDashoffset = `${RING_C}`;
    });
  }, [active]);

  // The countdown itself. Pausing simply tears the loop down and banks the time
  // served, so hovering holds the arc where it is and resuming carries on from
  // there. The ring is written straight to the DOM so a 60fps sweep never
  // re-renders the cards behind it.
  useEffect(() => {
    if (count <= 1) return;

    // Auto-advancing carousels are disorienting under reduced motion.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    if (!inView || hovered || dragging) return;

    let frame = 0;
    let startedAt: number | undefined;
    let served = elapsedRef.current;

    const tick = (now: number) => {
      if (startedAt === undefined) startedAt = now;
      served = elapsedRef.current + (now - startedAt);
      const progress = Math.min(1, served / AUTOPLAY_MS);

      // Two slots can be showing the ring at once: the one sliding into the
      // centre on mobile, and the middle copy the desktop row is pinned to.
      // They are the same slot except during a slide, so this usually writes
      // the same node twice.
      const offset = `${RING_C * (1 - progress)}`;
      [ringRefs.current[pos], ringRefs.current[middle]].forEach((ring) => {
        if (ring) ring.style.strokeDashoffset = offset;
      });

      if (progress >= 1) {
        step(1);
        return;
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      elapsedRef.current = Math.min(served, AUTOPLAY_MS);
    };
    // `pos`, not `active`: re-centring swaps which strip slot holds the live
    // ring, and the loop has to follow it there. It resumes from the banked
    // time, so the sweep carries straight on across the swap.
  }, [pos, middle, count, inView, hovered, dragging]);

  if (count === 0) return null;

  const strip = Array.from({ length: count * STRIP_SETS }, (_, i) => items[i % count]);
  const slide = recentring ? "" : "transition-transform duration-500 ease-[cubic-bezier(0.65,0,0.35,1)]";

  return (
    <section className="w-full overflow-x-clip bg-black pt-[8rem] lg:pt-[21.5rem]">
      <div className="flex w-full flex-col items-center gap-[3rem] lg:gap-[4rem]">
        {/* ── Heading ──────────────────────────────────────────────────── */}
        <div className="flex flex-col items-center gap-[2.5rem] px-[2rem] text-center lg:gap-[4rem] lg:px-[45rem]">
          {chip && (
            <FadeIn delay={0}>
              <PipeChip>{chip}</PipeChip>
            </FadeIn>
          )}
          <div className="flex flex-col items-center gap-[2rem] lg:gap-[3.5rem]">
            {title && (
              <FadeIn
                delay={0.1}
                className="richtext richtext-center max-w-full [text-wrap:balance] lg:w-[62.625rem] prose-p:m-0 prose-p:font-news prose-p:text-[3.75rem] prose-p:leading-[1.1] prose-p:tracking-[-0.15rem] prose-p:text-white/90 lg:prose-p:text-[6rem] lg:prose-p:tracking-[-0.25rem] prose-em:font-news prose-em:italic prose-headings:m-0 prose-headings:font-news prose-headings:text-[4.5rem] prose-headings:leading-[1.1] prose-headings:tracking-[-0.15rem] prose-headings:text-white/90 lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.25rem]">
                <PortableText
                  value={title}
                  components={richTextComponent}
                />
              </FadeIn>
            )}
            {subtitle && (
              <FadeIn delay={0.15}>
                <p className="m-0 max-w-full text-center font-geist text-[2rem] leading-[1.5] text-white lg:w-[53.625rem] lg:text-[2.25rem]">
                  {subtitle}
                </p>
              </FadeIn>
            )}
          </div>
        </div>

        {/* ── Avatar picker ────────────────────────────────────────────── */}
        <FadeIn
          delay={0.2}
          className="w-full lg:w-auto">
          <div
            ref={pickerRef}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className="mb-[3rem] flex w-full items-center gap-[1.5rem] lg:w-auto lg:gap-[0.875rem]">
            <button
              type="button"
              aria-label="Previous discipline"
              onClick={() => step(-1)}
              className="flex h-[3rem] w-[3rem] shrink-0 items-center justify-center text-white transition-opacity hover:opacity-70">
              <ChevronIcon className="h-[1.07rem] w-[1.875rem] rotate-90" />
            </button>

            {/* Mobile viewport: clips the overflowing row and fades both edges
                so avatars dissolve rather than getting cut off. `lg:contents`
                dissolves this wrapper and the track below on desktop, where the
                avatars are direct flex children of the row again — a box with
                `display: contents` generates no box at all, so the mask, the
                clipping and the track's transform all stop applying there. */}
            <div className="relative h-[8.125rem] min-w-0 flex-1 overflow-hidden [mask-image:linear-gradient(to_right,transparent_0,#000_4rem,#000_calc(100%_-_4rem),transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_0,#000_4rem,#000_calc(100%_-_4rem),transparent_100%)] lg:contents">
              <div
                className={`flex absolute top-0 left-1/2 items-center gap-[1.5rem] will-change-transform lg:contents ${slide}`}
                style={{
                  transform: `translateX(-${pos * AVATAR_STEP_REM + AVATAR_REM / 2}rem)`,
                }}>
                {strip.map((item, i) => {
                  // The live slot differs per breakpoint: mobile slides the
                  // strip, so it is whichever slot is heading for the centre;
                  // desktop drops the outer copies, so it is the middle one.
                  // Both are marked and the `lg:` variants pick the winner.
                  const isActive = i === pos;
                  const isActiveDesktop = i === middle;
                  const isCopy = i < count || i >= count * 2;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPos(i)}
                      aria-label={item.category ?? `Discipline ${(i % count) + 1}`}
                      aria-current={isActiveDesktop}
                      aria-hidden={isCopy}
                      tabIndex={isCopy ? -1 : undefined}
                      className={`flex relative justify-center items-center rounded-full shrink-0 w-[8.125rem] h-[8.125rem] ${
                        isCopy ? "lg:hidden" : ""
                      }`}>
                      {/* Countdown ring — faint track plus the sweeping arc, drawn
                      from 12 o'clock. Only the active avatar shows it. */}
                      <svg
                        viewBox="0 0 65 65"
                        aria-hidden
                        className={`absolute inset-0 w-full h-full -rotate-90 transition-opacity duration-300 ${
                          isActive ? "opacity-100" : "opacity-0"
                        } ${isActiveDesktop ? "lg:opacity-100" : "lg:opacity-0"}`}>
                        <circle
                          cx="32.5"
                          cy="32.5"
                          r={RING_R}
                          fill="none"
                          stroke="#A7DDFF"
                          strokeOpacity="0.25"
                          strokeWidth="1"
                        />
                        <circle
                          ref={(node) => {
                            ringRefs.current[i] = node;
                          }}
                          cx="32.5"
                          cy="32.5"
                          r={RING_R}
                          fill="none"
                          stroke="#A7DDFF"
                          strokeOpacity="0.9"
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeDasharray={RING_C}
                          strokeDashoffset={RING_C}
                        />
                      </svg>

                      <span
                        className={`overflow-x-clip relative rounded-full bg-white/10 transition-opacity duration-300 w-[7.125rem] h-[7.125rem] ${
                          isActive ? "opacity-100" : "opacity-50 hover:opacity-80"
                        } ${isActiveDesktop ? "lg:opacity-100" : "lg:opacity-50 lg:hover:opacity-80"}`}>
                        {item.avatar && (
                          <Image
                            src={item.avatar.src}
                            alt=""
                            fill
                            quality={90}
                            sizes="7.125rem"
                            className="object-cover"
                          />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              aria-label="Next discipline"
              onClick={() => step(1)}
              className="flex h-[3rem] w-[3rem] shrink-0 items-center justify-center text-white transition-opacity hover:opacity-70">
              <ChevronIcon className="h-[1.07rem] w-[1.875rem] -rotate-90" />
            </button>
          </div>
        </FadeIn>

        {/* ── Cards ────────────────────────────────────────────────────── */}
        {/* Desktop — sliding track centred on the active slot */}
        {/* No fixed band height: browser zoom rewraps the card copy through the
            vw-driven rem scale, so a hardcoded 86.75rem sometimes ran shorter
            than the cards and cropped them. The track sits in flow (relative,
            not absolute), so the band is always exactly as tall as the tallest
            card; the section's overflow-x-clip still swallows the sideways
            overhang. */}
        <div
          className={`relative hidden w-full select-none [touch-action:pan-y] lg:block ${
            dragging ? "cursor-grabbing" : "cursor-grab"
          }`}
          onPointerDown={onDragDown}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          onDragStart={(e) => e.preventDefault()}>
          <div
            className={`flex relative left-1/2 items-center pb-[2.5rem] will-change-transform ${
              dragging ? "" : slide
            }`}
            style={{
              transform: `translateX(calc(-${pos * STEP_REM + SLOT_REM / 2}rem + ${dragging ? dragPx : 0}px))`,
            }}>
            {strip.map((item, i) => (
              <div
                key={i}
                className="shrink-0"
                style={{
                  width: `${SLOT_REM}rem`,
                  marginRight: i === strip.length - 1 ? 0 : `${STEP_REM - SLOT_REM}rem`,
                }}>
                <button
                  type="button"
                  onClick={() => setPos(i)}
                  aria-label={item.category ?? `Discipline ${(i % count) + 1}`}
                  aria-hidden={i < count || i >= count * 2}
                  tabIndex={i < count || i >= count * 2 ? -1 : undefined}
                  className="block w-full cursor-pointer text-left">
                  <DisciplineCard
                    item={item}
                    active={i === pos}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile — the same sliding, draggable strip, one viewport-wide slot
            per card (100vw − 4rem, 1.5rem gap) so neighbours peek in at the
            edges. The slot width is linear in vw and rem, so the centring
            transform is a calc() and nothing needs measuring; the shared drag
            handlers settle with the mobile pitch. */}
        <div
          className="w-full overflow-hidden select-none [touch-action:pan-y] lg:hidden"
          onPointerDown={onDragDown}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          onDragStart={(e) => e.preventDefault()}>
          <div
            className={`flex relative left-1/2 items-center gap-[1.5rem] will-change-transform ${
              dragging ? "" : slide
            }`}
            style={{
              // x = −(pos·(slot+gap) + slot/2), expanded into rem and vw terms.
              transform: `translateX(calc(${2.5 * pos + 2}rem - ${100 * pos + 50}vw + ${dragging ? dragPx : 0}px))`,
            }}>
            {strip.map((item, i) => (
              <div
                key={i}
                className="w-[calc(100vw-4rem)] shrink-0">
                <DisciplineCard
                  item={item}
                  active={i === pos}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
